import { useCallback, useRef, useState } from 'react';
import { SubmitHandler, UseFormHandleSubmit } from 'react-hook-form';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useSuggestionAcknowledgementContext } from '@/modules/aiSuggestions/context/SuggestionAcknowledgement.context';
import { useAcknowledgeAiSuggestionsMutation } from '@/modules/aiSuggestions/graphql/aiSuggestions.generated';
import { useCanEditDispositiveProvisions } from '@/modules/authentication/hooks/useCanEditDispositiveProvisions';
import {
  Operations,
  useCreateBeneficiaryDesignationGroupMutation,
  useUpdateBeneficiaryDesignationGroupMutation,
} from '@/modules/beneficiaryDesignations/graphql/BeneficiaryDesignations.generated';
import { Operations as BeneficiaryDesignationSuggestionsOperations } from '@/modules/beneficiaryDesignations/graphql/BeneficiaryDesignationSuggestions.generated';
import { Operations as GetDispositiveProvisionsOperations } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/graphql/GetDispositiveProvisions.generated';
import { EditEntitySection } from '@/modules/entities/EditEntitySplitScreen/EditEntitySplitScreen.types';
import { Operations as DispositionsDiagramOperations } from '@/modules/entityDiagram/graphql/DispositionsDiagram.generated';
import { Operations as EstateWaterfallOperations } from '@/modules/estateWaterfall/graphql/EstateWaterfall.generated';
import { Operations as GetWaterfallSummaryOperations } from '@/modules/estateWaterfall/graphql/GetWaterfallSummary.generated';
import { Operations as ShowWaterfallSummaryTileDetailsOperations } from '@/modules/estateWaterfall/graphql/ShowWaterfallSummaryTileDetails.generated';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { diagnostics } from '@/utils/diagnostics';

import {
  BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE,
  BeneficiaryDesignationsFormShape,
} from '../BeneficiaryDesignationsForm.types';
import { makeBdgMutationInputForTier } from '../BeneficiaryDesignationsForm.utils';

interface UseSubmitBeneficiaryDesignationsOpts {
  entityId: string | null;
  entitySubtypeId: string;
  entityType: 'retirement-account' | 'insurance-account';
  isOnHypotheticalWaterfall: boolean;
  waterfallId?: string;
  initialFormValues: BeneficiaryDesignationsFormShape;
  onAfterSubmit: (afterSubmitSection?: EditEntitySection) => void;
  onClose: () => void;
  /**
   * Called after a successful persist. Hosts (e.g. the presentation designer)
   * use it to flag that an edit landed so they refresh derived data on close —
   * otherwise the entity-overview slide stays stale until a full page reload.
   */
  onDidSave?: () => void;
  // Passed in from the parent because this hook is called outside the
  // FormProvider, so useFormContext() would return null.
  handleSubmit: UseFormHandleSubmit<BeneficiaryDesignationsFormShape>;
}

export function useSubmitBeneficiaryDesignations({
  entityId,
  entitySubtypeId,
  entityType,
  isOnHypotheticalWaterfall,
  waterfallId,
  initialFormValues,
  onAfterSubmit,
  onClose: _onClose,
  onDidSave,
  handleSubmit,
}: UseSubmitBeneficiaryDesignationsOpts) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { householdId } = useHouseholdDetailsContext();
  const canEditDP = useCanEditDispositiveProvisions();

  // AI beneficiary-designation suggestions accepted/ignored this session. The
  // accepted ones' recipients are already in the form (and saved by the BDG
  // mutations below); here we flip every acknowledged suggestion's status via
  // acknowledgeAISuggestions so it stops resurfacing in the confirm card.
  const { acknowledgedSuggestions } = useSuggestionAcknowledgementContext();
  const [acknowledgeAiSuggestions] = useAcknowledgeAiSuggestionsMutation({
    refetchQueries: [
      BeneficiaryDesignationSuggestionsOperations.Query
        .FetchBeneficiaryDesignationSuggestions,
    ],
    awaitRefetchQueries: true,
    onError: (error) => {
      reportError('Failed to acknowledge AI beneficiary suggestions', error);
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track which button was used to submit so we can route after save
  const afterSubmitSectionRef = useRef<EditEntitySection | undefined>(
    undefined
  );

  const bdgRefetchQueries = [
    Operations.Query.GetBeneficiaryDesignations,
    GetDispositiveProvisionsOperations.Query.GetDispositiveProvisions,
    DispositionsDiagramOperations.Query.DispositionsDiagram,
    EstateWaterfallOperations.Query.EstateWaterfall,
    GetWaterfallSummaryOperations.Query.GetWaterfallSummary,
    ShowWaterfallSummaryTileDetailsOperations.Query
      .ShowWaterfallSummaryTileDetails,
  ];

  const [createBdg] = useCreateBeneficiaryDesignationGroupMutation({
    refetchQueries: bdgRefetchQueries,
    awaitRefetchQueries: true,
    onError: (error) => {
      reportError('Failed to create beneficiary designation group', error);
    },
  });

  const [updateBdg] = useUpdateBeneficiaryDesignationGroupMutation({
    refetchQueries: bdgRefetchQueries,
    awaitRefetchQueries: true,
    onError: (error) => {
      reportError('Failed to update beneficiary designation group', error);
    },
  });

  // Runs the create/update mutations for whichever tiers changed. Returns
  // whether anything was actually persisted so callers can decide whether to
  // show a "saved" confirmation (a no-op happens when navigating away from an
  // unedited form).
  const runMutations = useCallback(
    async (
      formValues: BeneficiaryDesignationsFormShape
    ): Promise<{ saved: boolean }> => {
      if (!canEditDP || !householdId || !entityId) return { saved: false };

      const currentPrimary =
        formValues[BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE].primary;
      const currentContingent =
        formValues[BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE].contingent;
      const initialPrimary =
        initialFormValues[BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE].primary;
      const initialContingent =
        initialFormValues[BENEFICIARY_DESIGNATIONS_FORM_NAMESPACE].contingent;

      const mutationOpts = {
        householdId,
        isOnHypotheticalWaterfall,
        waterfallId,
        entityId,
        entityType,
        entitySubtypeId,
      };

      const primaryInput = makeBdgMutationInputForTier(
        currentPrimary,
        initialPrimary,
        mutationOpts
      );
      const contingentInput = makeBdgMutationInputForTier(
        currentContingent,
        initialContingent,
        mutationOpts
      );

      diagnostics.debug(
        '[useSubmitBeneficiaryDesignations] Submitting BDG mutations',
        { primaryInput, contingentInput }
      );

      const mutations: Promise<{ data?: unknown | null }>[] = [];

      if (primaryInput.type === 'create') {
        mutations.push(createBdg({ variables: { input: primaryInput.input } }));
      } else if (primaryInput.type === 'update') {
        mutations.push(updateBdg({ variables: { input: primaryInput.input } }));
      }

      if (contingentInput.type === 'create') {
        mutations.push(
          createBdg({ variables: { input: contingentInput.input } })
        );
      } else if (contingentInput.type === 'update') {
        mutations.push(
          updateBdg({ variables: { input: contingentInput.input } })
        );
      }

      // Persist accepted/ignored AI suggestions. This can fire even when no
      // tier changed (e.g. the user only ignored suggestions), so it's counted
      // toward `saved` independently of the BDG mutations.
      const acknowledgeSuggestionsInput = Object.values(
        acknowledgedSuggestions
      ).map((s) => ({
        suggestionID: s.suggestionID,
        status: s.status,
        clientProfileID: s.clientProfileId,
        clientOrganizationID: s.clientOrganizationId,
        entityID: s.entityId,
        testamentaryEntityID: s.testamentaryEntityId,
      }));
      if (acknowledgeSuggestionsInput.length > 0) {
        mutations.push(
          acknowledgeAiSuggestions({
            variables: { input: acknowledgeSuggestionsInput },
          })
        );
      }

      if (mutations.length === 0) return { saved: false };

      const results = await Promise.all(mutations);
      // These mutations set onError, so Apollo resolves the mutate promise with
      // `{ data: undefined }` instead of rejecting on failure; inspect the
      // results so a failed save throws and the callers' catch blocks run.
      if (results.some((r) => !r?.data)) {
        throw new Error('Beneficiary designation save failed');
      }
      // Signal a successful persist so hosts can refresh derived data (e.g. the
      // presentation designer's entity-overview context). Both the form submit
      // and the diagram-tile save route through here.
      onDidSave?.();
      return { saved: true };
    },
    [
      canEditDP,
      householdId,
      isOnHypotheticalWaterfall,
      waterfallId,
      entityId,
      entityType,
      entitySubtypeId,
      initialFormValues,
      createBdg,
      updateBdg,
      acknowledgedSuggestions,
      acknowledgeAiSuggestions,
      onDidSave,
    ]
  );

  const submitForm: SubmitHandler<BeneficiaryDesignationsFormShape> =
    useCallback(
      async (formValues) => {
        if (!canEditDP) return;
        setIsSubmitting(true);
        try {
          const { saved } = await runMutations(formValues);
          if (saved) {
            showFeedback('Beneficiary designations saved successfully', {
              variant: 'success',
            });
          }
          onAfterSubmit(afterSubmitSectionRef.current);
        } catch (error) {
          diagnostics.error(
            '[useSubmitBeneficiaryDesignations] Error submitting BDG form',
            error instanceof Error ? error : null
          );
          showFeedback(
            'Failed to save beneficiary designations. Please try again.',
            { variant: 'error' }
          );
        } finally {
          setIsSubmitting(false);
        }
      },
      [canEditDP, runMutations, showFeedback, onAfterSubmit]
    );

  const onSubmit = useCallback(
    (afterSubmitSection?: EditEntitySection) => {
      if (!canEditDP) {
        onAfterSubmit(afterSubmitSection);
        return;
      }
      afterSubmitSectionRef.current = afterSubmitSection;
      void handleSubmit(submitForm)();
    },
    [canEditDP, onAfterSubmit, handleSubmit, submitForm]
  );

  // Tile-click variant: validate and persist (if anything changed) WITHOUT
  // closing or switching sections, mirroring the DP form's
  // `handleDiagramTileClickSubmission` contract so the shared diagram
  // node-selection handler can save before swapping the right pane.
  const handleDiagramTileClickSubmission = useCallback(
    (): Promise<{ success: boolean; submit?: () => Promise<void> }> =>
      new Promise((resolve) => {
        if (!canEditDP) {
          resolve({ success: true });
          return;
        }
        void handleSubmit(
          (formValues) =>
            resolve({
              success: true,
              submit: async () => {
                setIsSubmitting(true);
                try {
                  const { saved } = await runMutations(formValues);
                  if (saved) {
                    showFeedback(
                      'Beneficiary designations saved successfully',
                      {
                        variant: 'success',
                      }
                    );
                  }
                } finally {
                  setIsSubmitting(false);
                }
              },
            }),
          () => resolve({ success: false })
        )();
      }),
    [canEditDP, handleSubmit, runMutations, showFeedback]
  );

  return {
    onSubmit,
    isSubmitting,
    handleDiagramTileClickSubmission,
  };
}
